import { Form, Popup, Spacing } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TwoFaActionWizard } from 'shared/components';
import { GetBeneficiarySchema } from 'shared/data/webApp/v1';

import {
    useBeneficiarySchema,
    useCreateBeneficiaryMutation,
    useNestedPopups,
    useReloadSchemaIfLocalClearingFieldIsRequired,
} from './AddBeneficiaryPopupContent.hooks';
import { messages } from './AddBeneficiaryPopupContent.messages';
import { getFieldsValidationSchema } from './AddBeneficiaryPopupContent.schema';
import {
    AddBeneficiaryPopupProps,
    BeneficiaryPayload,
    FormValues,
    ValidationSchema,
} from './AddBeneficiaryPopupContent.types';
import { Loading } from './components/Loading/Loading';
import { NavigationButtons } from './components/NavigationButtons/NavigationButtons';
import { StepFour } from './components/StepFour/StepFour';
import { StepOne } from './components/StepOne/StepOne';
import StepsIndicator from './components/StepsIndicator/StepsIndicator';
import { StepThree } from './components/StepThree/StepThree';
import { StepTwo } from './components/StepTwo/StepTwo';
import { SuccessPopupContent } from './components/SuccessPopupContent/SuccessPopupContent';

export const AddBeneficiaryPopupContent = memo<AddBeneficiaryPopupProps>((props) => {
    const { closePopup, initialCurrency, initialBillId } = props;

    const { isOpen2FA, setIsOpen2FA, close2FAPopup, setIsOpenSuccessPopup, isOpenSuccessPopup } = useNestedPopups();

    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [beneficiaryPayload, setBeneficiaryPayload] = useState<BeneficiaryPayload>({});
    const [activeStep, setActiveStep] = useState(1);

    const request = useSelector(requestSelector);
    const integration = useSelector((state) =>
        selectors.integration.getIntegrationByCompanyId(state, request.companyId)
    );

    const [validationSchema, setValidationSchema] = useState<ValidationSchema>();

    const form = useForm<FormValues>({ mode: 'onBlur', resolver: validationSchema && zodResolver(validationSchema) });

    const [fieldsSchemaTransfer, setFieldsSchemaTransfer] = useState<GetBeneficiarySchema['params']['query']>({
        companyId: request.companyId,
        accountCurrency: initialCurrency,
    });

    const { beneficiarySchema, isFetchingBeneficiarySchema } = useBeneficiarySchema({
        form,
        initialCurrency,
        closePopup,
        countryCode: integration?.countryCode,
        activeStep,
        setActiveStep,
        fieldsSchemaTransfer,
        setFieldsSchemaTransfer,
    });

    useEffect(() => {
        beneficiarySchema && setValidationSchema(getFieldsValidationSchema(beneficiarySchema, activeStep));
    }, [beneficiarySchema, activeStep]);

    const { mutateAsync: createBeneficiaryApiCall, isLoading: isLoadingCreateBeneficiary } =
        useCreateBeneficiaryMutation({
            setIsOpenSuccessPopup,
            setIsOpen2FA,
            setErrorMessages,
            initialBillId,
            beneficiarySchema,
        });

    useReloadSchemaIfLocalClearingFieldIsRequired({
        activeStep,
        form,
        beneficiarySchema,
        fieldsSchemaTransfer,
        companyId: request.companyId,
        setFieldsSchemaTransfer,
    });

    const onEnterAppCode = useCallback(
        async (code: string) => {
            await createBeneficiaryApiCall({
                body: {
                    companyId: request.companyId,
                    requestId: request.id,
                    oneTimePassword: code,
                    beneficiaryFields: {
                        ...beneficiaryPayload,
                        payment_methods: [beneficiaryPayload.payment_methods || ''],
                    },
                },
            }).catch(() => {});
        },
        [beneficiaryPayload, createBeneficiaryApiCall, request.companyId, request.id]
    );

    const onSubmit = form.handleSubmit((data) => {
        setErrorMessages([]);
        setIsOpen2FA(true);
        setBeneficiaryPayload(data);
    });

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                progress={isLoadingCreateBeneficiary}
                title={messages.popupTitle}
                actions={
                    <NavigationButtons
                        activeStep={activeStep}
                        isLoadingFieldsSchema={isFetchingBeneficiarySchema}
                        setActiveStep={setActiveStep}
                        setErrorMessages={setErrorMessages}
                        fieldsSchema={beneficiarySchema}
                        fieldsSchemaTransfer={fieldsSchemaTransfer}
                        setFieldsSchemaTransfer={setFieldsSchemaTransfer}
                    />
                }
            />

            <Popup.Body>
                <StepsIndicator activeStep={activeStep - 1} />

                {isFetchingBeneficiarySchema ? (
                    <Loading />
                ) : (
                    <>
                        <Spacing height={32} />

                        {activeStep === 1 && <StepOne fields={beneficiarySchema} />}

                        {activeStep === 2 && !isFetchingBeneficiarySchema && <StepTwo fields={beneficiarySchema} />}

                        {activeStep === 3 && !isFetchingBeneficiarySchema && <StepThree fields={beneficiarySchema} />}

                        {activeStep === 4 && (
                            <StepFour
                                fields={beneficiarySchema}
                                errorMessages={errorMessages}
                                isLoading={isLoadingCreateBeneficiary}
                            />
                        )}
                    </>
                )}

                <TwoFaActionWizard
                    title={messages.twoFaPopupTitle}
                    open={isOpen2FA}
                    onEnterAppCode={onEnterAppCode}
                    onClose={close2FAPopup}
                    isLoading={isLoadingCreateBeneficiary}
                />
            </Popup.Body>

            <Popup open={isOpenSuccessPopup} closable={false} size='xsmall'>
                <SuccessPopupContent onClose={closePopup} />
            </Popup>
        </Form>
    );
});

AddBeneficiaryPopupContent.displayName = 'AddBeneficiaryPopupContent';
